import { eventBus, getFeatureFlag, getLocalDataEventName, getLocalDataSnapshot, useFeatureFlag, useLocalData } from '@libs/core';
import { EFeatureFlag, LocalDataKeys } from '@libs/types';

/**
 * Local-data key of the manual (QA) override. Stored by `useLocalData` as `ps3_local_data:rebranding`, so it can be
 * flipped from DevTools — `localStorage.setItem('ps3_local_data:rebranding', 'true')` and reload — or live via the
 * `save_local_data:rebranding` player action. Remove the item to go back to the backend flag.
 */
export const REBRANDING_OVERRIDE_KEY = 'rebranding';

const readRebranding = (): boolean => {
    const override = getLocalDataSnapshot<unknown>(REBRANDING_OVERRIDE_KEY);
    return typeof override === 'boolean' ? override : (getFeatureFlag(EFeatureFlag.Rebranding) ?? false);
};

// Called for every color/icon lookup of a widget without a version, so the localStorage reads are cached. Both
// sources change only through `saveLocalData`, which broadcasts the events that drop the cache. Subscribed lazily,
// not at import: many unit tests mock `@libs/core` without `eventBus`.
let cached: boolean | undefined;
let subscribed = false;
const dropCache = () => {
    cached = undefined;
};
const subscribeOnce = () => {
    if (!subscribed) {
        subscribed = true;
        eventBus.subscribe(getLocalDataEventName(REBRANDING_OVERRIDE_KEY), 'getRebranding', dropCache);
        eventBus.subscribe(getLocalDataEventName(LocalDataKeys.featureFlags), 'getRebranding', dropCache);
    }
};
const isAvailable = () => typeof eventBus?.subscribe === 'function' && typeof getLocalDataSnapshot === 'function' && typeof getFeatureFlag === 'function';

/**
 * Synchronous snapshot getter for the rebranding state outside of React render cycles
 * (e.g. in URL generators, preloader utilities).
 *
 * NOTE (Known Limitation): This function is non-reactive. It returns the current state (local override, else the
 * persisted backend flag) without subscribing the caller to subsequent updates. Callers within React render trees
 * that must update dynamically when the flag or QA override changes at runtime should use `useRebranding()` instead.
 * A direct `localStorage` edit (DevTools) is seen after a reload.
 */
export const getRebranding = (): boolean => {
    if (cached !== undefined) {
        return cached;
    }
    if (!isAvailable()) {
        // `@libs/core` is mocked without these (unit tests): the switch is off and nothing is cached.
        return false;
    }
    subscribeOnce();
    cached = readRebranding();
    return cached;
};

/** Drops the cached snapshot, for tests that write `localStorage` directly. */
export const resetRebrandingCacheForTesting = (): void => dropCache();

/**
 * Whether widgets render in the new (rebranded) design.
 *
 * The single place that decides it — widgets call this hook rather than `useFeatureFlag` directly,
 * so the source of the decision can change without touching them.
 *
 * @returns the manual override when it is a boolean, otherwise the last `configuration.featureFlags.rebranding`
 * received with `requestInteraction`, persisted across reloads (`false` until it ever arrives)
 */
export const useRebranding = (): boolean => {
    const override = useLocalData<unknown>(REBRANDING_OVERRIDE_KEY);
    const serverFlag = useFeatureFlag(EFeatureFlag.Rebranding);

    return typeof override === 'boolean' ? override : serverFlag;
};
